import * as React from 'react';
import { CheckedState } from '@radix-ui/react-checkbox';
import { Checkbox } from 'components/ui/checkbox';
import { cn } from 'lib/utils';
import { PageHeaderWithCheckboxesContext } from './context';
import type { CheckboxOption } from './types';
import { Skeleton } from '../skeleton';

export const Checkboxes = () => {
  const { checkboxesConfig: {
    toggleAllLabel = 'Todos',
    hideToggleAll = false,
    options = [],
  }, selectedCheckboxValues, setSelectedCheckboxValues } =
    React.useContext(PageHeaderWithCheckboxesContext);


  const selectionState = React.useMemo(() => {
    switch (true) {
      case selectedCheckboxValues.length === options.length:
        return 'all';
      case selectedCheckboxValues.length > 0:
        return 'some';
      case selectedCheckboxValues.length === 0:
      default:
        return 'none';
    }
  }, [options, selectedCheckboxValues]);

  const toggleOption = React.useCallback(
    (checked: CheckedState, option: CheckboxOption) => {
      setSelectedCheckboxValues((selected) => {
        return checked
          ? [...selected, option.value]
          : selected.filter((value) => value !== option.value);
      });
    },
    [setSelectedCheckboxValues]
  );

  const toggleAll = React.useCallback(() => {
    if (selectionState === 'all') {
      setSelectedCheckboxValues([]);
    } else {
      setSelectedCheckboxValues(options.map((option) => option.value));
    }
  }, [selectionState, setSelectedCheckboxValues]);

  if (!options.length) return null;

  return (
    <div className={'flex flex-row gap-3'}>
      {!hideToggleAll && !!options.length && (
        <div className={'flex items-center gap-2'}>
          <Checkbox
            id={`toggle-all`}
            checked={selectionState === 'all'}
            onCheckedChange={toggleAll}
          />
          <Label htmlFor={`toggle-all`}>{toggleAllLabel}</Label>
        </div>
      )}
      {options.map((option) => (
        <div key={option.value} className={'flex items-center gap-2'}>
          <Checkbox
            id={`checkbox-${option.value}`}
            checked={selectedCheckboxValues.includes(option.value)}
            onCheckedChange={(checked) => toggleOption(checked, option)}
          />
          <Label htmlFor={`checkbox-${option.value}`}>{option.label}</Label>
        </div>
      ))}
    </div>
  );
};

const Label = ({ className, ...props }: React.ComponentProps<'label'>) => {
  return (
    <label
      className={cn(
        'font-GilroySemibold text-sm cursor-pointer font-semibold text-slate-600 leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70',
        className
      )}
      {...props}
    />
  );
};

Checkboxes.Skeleton = function () {
  return (
    <div className={'flex flex-row items-center gap-4'}>
      <CheckboxSkeleton />
      <CheckboxSkeleton />
      <CheckboxSkeleton />
      <CheckboxSkeleton />
      <CheckboxSkeleton />
    </div>
  )
};

const CheckboxSkeleton = () => (
  <div className="flex flex-row items-center gap-2">
    <Skeleton className={'w-4 h-4'} />
    <Skeleton className={'w-20 h-4'} />
  </div>
)